import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserProfile, userManagementHelpers, supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

interface UsersContextType {
  users: UserProfile[];
  loading: boolean;
  error: string | null;
  refreshUsers: () => Promise<void>;
  promoteToAdmin: (userEmail: string) => Promise<{ success: boolean; message: string }>;
  demoteAdmin: (userEmail: string) => Promise<{ success: boolean; message: string }>;
  toggleUserStatus: (userEmail: string) => Promise<{ success: boolean; message: string }>;
  getUserByEmail: (email: string) => Promise<UserProfile | null>;
  approveSupplier: (userEmail: string) => Promise<{ success: boolean; message: string }>;
  demoteSupplier: (userEmail: string) => Promise<{ success: boolean; message: string }>;
  promoteToMentor: (userEmail: string) => Promise<{ success: boolean; message: string }>;
  demoteMentor: (userEmail: string) => Promise<{ success: boolean; message: string }>;
}

const UsersContext = createContext<UsersContextType | undefined>(undefined);

export const useUsers = () => {
  const context = useContext(UsersContext);
  if (!context) {
    throw new Error('useUsers deve ser usado dentro de um UsersProvider');
  }
  return context;
};

interface UsersProviderProps {
  children: React.ReactNode;
}

export const UsersProvider: React.FC<UsersProviderProps> = ({ children }) => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAdmin } = useAuth();

  // Carregar usuários
  const loadUsers = async () => {
    if (!isAdmin) {
      console.log('🔒 Usuário não é admin, não carregando usuários');
      setUsers([]);
      return;
    }

    console.log('👥 Carregando usuários...');
    setLoading(true);
    setError(null);

    try {
      // Usar uma consulta mais simples para evitar problemas de RLS
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Erro ao carregar usuários:', error);
        setError('Erro ao carregar usuários');
        setUsers([]);
      } else {
        console.log('✅ Usuários carregados:', data?.length || 0);
        setUsers(data || []);
      }
    } catch (error) {
      console.error('❌ Erro inesperado ao carregar usuários:', error);
      setError('Erro inesperado ao carregar usuários');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Recarregar usuários
  const refreshUsers = async () => {
    await loadUsers();
  };

  // Promover usuário a admin
  const promoteToAdmin = async (userEmail: string): Promise<{ success: boolean; message: string }> => {
    console.log('👑 Tentando promover usuário:', userEmail);
    try {
      const result = await userManagementHelpers.promoteToAdmin(userEmail);
      console.log('📊 Resultado da promoção:', result);
      
      if (result.success) {
        // Recarregar lista de usuários
        await refreshUsers();
      }
      
      return result;
    } catch (error) {
      console.error('❌ Erro ao promover usuário:', error);
      return { success: false, message: 'Erro inesperado ao promover usuário' };
    }
  };

  // Rebaixar admin a usuário
  const demoteAdmin = async (userEmail: string): Promise<{ success: boolean; message: string }> => {
    console.log('👤 Tentando rebaixar admin:', userEmail);
    try {
      const result = await userManagementHelpers.demoteAdmin(userEmail);
      console.log('📊 Resultado do rebaixamento:', result);
      
      if (result.success) {
        // Recarregar lista de usuários
        await refreshUsers();
      }
      
      return result;
    } catch (error) {
      console.error('❌ Erro ao rebaixar admin:', error);
      return { success: false, message: 'Erro inesperado ao rebaixar admin' };
    }
  };

  // Suspender/reativar usuário
  const toggleUserStatus = async (userEmail: string): Promise<{ success: boolean; message: string }> => {
    try {
      const result = await userManagementHelpers.toggleUserStatus(userEmail);
      
      if (result.success) {
        // Recarregar lista de usuários
        await refreshUsers();
      }
      
      return result;
    } catch (error) {
      console.error('Erro ao alterar status do usuário:', error);
      return { success: false, message: 'Erro inesperado ao alterar status do usuário' };
    }
  };

  // Buscar usuário por email
  const getUserByEmail = async (email: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await userManagementHelpers.getUserByEmail(email);
      
      if (error) {
        console.error('Erro ao buscar usuário por email:', error);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Erro inesperado ao buscar usuário por email:', error);
      return null;
    }
  };

  // Aprovar fornecedor
  const approveSupplier = async (userEmail: string): Promise<{ success: boolean; message: string }> => {
    console.log('🚚 Tentando aprovar fornecedor:', userEmail);
    try {
      const result = await userManagementHelpers.approveSupplier(userEmail);
      if (result.success) {
        await refreshUsers();
      }
      return { success: result.success, message: result.message };
    } catch (error) {
      console.error('❌ Erro ao aprovar fornecedor:', error);
      return { success: false, message: 'Erro inesperado ao aprovar fornecedor' };
    }
  };

  // Rebaixar fornecedor
  const demoteSupplier = async (userEmail: string): Promise<{ success: boolean; message: string }> => {
    console.log('👤 Tentando rebaixar fornecedor:', userEmail);
    try {
      const result = await userManagementHelpers.demoteSupplier(userEmail);
      if (result.success) {
        await refreshUsers();
      }
      return { success: result.success, message: result.message };
    } catch (error) {
      console.error('❌ Erro ao rebaixar fornecedor:', error);
      return { success: false, message: 'Erro inesperado ao rebaixar fornecedor' };
    }
  };

  // Promover usuário a mentor
  const promoteToMentor = async (userEmail: string): Promise<{ success: boolean; message: string }> => {
    console.log('🎓 Tentando promover usuário a mentor:', userEmail);
    try {
      const result = await userManagementHelpers.promoteToMentor(userEmail);
      console.log('📊 Resultado da promoção a mentor:', result);
      
      if (result.success) {
        // Recarregar lista de usuários
        await refreshUsers();
      }
      
      return result;
    } catch (error) {
      console.error('❌ Erro ao promover usuário a mentor:', error);
      return { success: false, message: 'Erro inesperado ao promover usuário a mentor' };
    }
  };

  // Rebaixar mentor a usuário
  const demoteMentor = async (userEmail: string): Promise<{ success: boolean; message: string }> => {
    console.log('👤 Tentando rebaixar mentor:', userEmail);
    try {
      const result = await userManagementHelpers.demoteMentor(userEmail);
      console.log('📊 Resultado do rebaixamento de mentor:', result);
      
      if (result.success) {
        // Recarregar lista de usuários
        await refreshUsers();
      }
      
      return result;
    } catch (error) {
      console.error('❌ Erro ao rebaixar mentor:', error);
      return { success: false, message: 'Erro inesperado ao rebaixar mentor' };
    }
  };

  // Carregar usuários quando o contexto for montado ou quando o status de admin mudar
  useEffect(() => {
    loadUsers();
  }, [isAdmin]);

  const value: UsersContextType = {
    users,
    loading,
    error,
    refreshUsers,
    promoteToAdmin,
    demoteAdmin,
    toggleUserStatus,
    getUserByEmail,
    approveSupplier,
    demoteSupplier,
    promoteToMentor,
    demoteMentor,
  };

  return (
    <UsersContext.Provider value={value}>
      {children}
    </UsersContext.Provider>
  );
}; 